/**
 * =============================================================================
 * MonsterDeduction
 * =============================================================================
 *
 * Panel for tracking which species an opponent piece might be. Shows all 10
 * monster types in the same grid layout as MonsterList. Clicking toggles
 * “crossed off” state (xed). When nine species are crossed off, the remaining
 * type is set as the guess automatically. Clear resets crosses and the guess.
 *
 * Used by:
 * - GameplayPhase (opponent piece selected from the board)
 *
 * State is driven by selectedPiece from MonsterStateContext; updates flow through
 * onUpdateDeduction into local monster state (guess / xed). Local selectedMonster
 * holds the last species hovered in the grid (MonsterInfo preview for opponent pieces).
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { getAllMonsters } from '../../utils/constants';
import type { MonsterDefinition, MonsterTypeId, FrontendMonster, MonsterName } from '../../types/monster.types';
import type { Theme, PlayerLetter } from '../../types/game.types';
import MonsterCircle from './MonsterCircle';
import MonsterInfo, { MonsterInfoSubject } from './MonsterInfo';
import './MonsterList.css';
import { getMonsterName } from '../../utils/monsterHelpers';

export interface MonsterDeductionProps {
    selectedPiece: FrontendMonster | null;
    theme: Theme;
    myPlayerLetter: PlayerLetter;
    onUpdateDeduction: (
        piece: FrontendMonster,
        updates: { xed: MonsterTypeId[]; guess: MonsterTypeId | null }
    ) => void;
}

/** When exactly nine species are crossed off, returns the one remaining type as the inferred guess; otherwise null. */
function inferGuessWhenNineCrossed(xed: Set<MonsterTypeId>): MonsterTypeId | null {
    if (xed.size !== 9) return null;
    for (let t = 1; t <= 10; t++) {
        const id = t as MonsterTypeId;
        if (!xed.has(id)) return id;
    }
    return null;
}

/** Renders the deduction grid and Clear for the currently selected board piece (opponent only). */
export const MonsterDeduction: React.FC<MonsterDeductionProps> = ({
    selectedPiece,
    theme,
    myPlayerLetter,
    onUpdateDeduction,
}) => {
    const [hoveredTypeId, setHoveredTypeId] = useState<MonsterTypeId | null>(null);
    /** Last species hovered in the 10-monster grid; MonsterInfo uses this for opponent pieces (and grid preview). */
    const [selectedMonster, setSelectedMonster] = useState<MonsterDefinition | null>(null);
    const [imageDirection, setImageDirection] = useState<'up' | 'down'>('up');

    const definitions = useMemo(() => getAllMonsters(), []);

    const isMyPiece = selectedPiece?.player === myPlayerLetter;

    const xedSet = useMemo(() => {
        if (!selectedPiece) return new Set<MonsterTypeId>();
        return new Set<MonsterTypeId>(selectedPiece.xed);
    }, [selectedPiece]);

    useEffect(() => {
        console.log("changed");
        setSelectedMonster(null);
    }, [selectedPiece?.id, selectedPiece?.type]);

    const toggleImageDirection = useCallback(() => {
        setImageDirection((d) => (d === 'up' ? 'down' : 'up'));
    }, []);

    /** Toggles whether a species is crossed off; auto-fills guess when nine crosses leave one possibility. */
    const handleToggleType = useCallback(
        (def: MonsterDefinition) => {
            if (!selectedPiece || isMyPiece) return;

            const next = new Set(xedSet);
            if (next.has(def.id)) {
                next.delete(def.id);
            } else {
                next.add(def.id);
            }

            let guess: MonsterTypeId | null = null;
            const inferred = inferGuessWhenNineCrossed(next);
            if (inferred !== null) {
                guess = inferred;
            }

            onUpdateDeduction(selectedPiece, {
                xed: Array.from(next),
                guess,
            });
        },
        [selectedPiece, isMyPiece, xedSet, onUpdateDeduction]
    );

    /** Removes all crosses and clears the guess for the selected opponent piece. */
    const handleClear = useCallback(() => {
        if (!selectedPiece || isMyPiece) return;
        onUpdateDeduction(selectedPiece, { xed: [], guess: null });
    }, [selectedPiece, isMyPiece, onUpdateDeduction]);

    /** Visual treatment per species: hover, your-piece dimming, guess highlight, crossed-off dimming. */
    const getStyle = useCallback(
        (def: MonsterDefinition): React.CSSProperties => {
            const opacity = hoveredTypeId === def.id ? 0.75 : 1;

            if (!selectedPiece) {
                return { filter: 'brightness(1)', opacity };
            }

            if (isMyPiece) {
                const dim = selectedPiece.type !== def.id;
                return {
                    filter: dim ? 'brightness(0.5)' : 'brightness(1)',
                    opacity,
                };
            }

            if (selectedPiece.guess === def.id) {
                return { filter: 'brightness(1.5)', opacity };
            }
            if (xedSet.has(def.id)) {
                return { filter: 'brightness(0.5)', opacity };
            }
            return { filter: 'brightness(1)', opacity };
        },
        [selectedPiece, isMyPiece, xedSet, hoveredTypeId]
    );

    const infoMonster: MonsterInfoSubject | null = useMemo(() => {
        if (!selectedPiece) {
            return selectedMonster ? { type: selectedMonster.id, name: selectedMonster.name } : null;
        }
        if (selectedPiece.player === myPlayerLetter) {
            return { type: selectedPiece.type, name: selectedPiece.name };
        }
        if (selectedPiece.guess !== null) {
            return { type: selectedPiece.guess, name: getMonsterName(selectedPiece.guess) as MonsterName };
        }
        if (selectedMonster) {
            return { type: selectedMonster.id, name: selectedMonster.name };
        }
        return null;
    }, [selectedPiece, selectedMonster, myPlayerLetter]);

    return (
        <div className="monster_list_container">
            <div className="monster_option_container">
                <div className="monster_option_title">
                    <p className="monster_option_title_text">
                        {selectedPiece ?
                            isMyPiece ?
                                'Your piece'
                                : 'Possible monsters'
                            : 'Select an opponent piece to deduce'}
                    </p>
                </div>
                <div className="monster_option_body">
                    {definitions.map((def) => {
                        const style = getStyle(def);
                        const disabled = !selectedPiece || isMyPiece;

                        return (
                            <MonsterCircle
                                key={def.id}
                                monster={def}
                                theme={theme}
                                disabled={disabled}
                                onClick={() => handleToggleType(def)}
                                onMouseEnter={() => {
                                    setHoveredTypeId(def.id);
                                    setSelectedMonster(def);
                                }}
                                onMouseLeave={() => setHoveredTypeId(null)}
                                style={{
                                    cursor: disabled ? 'default' : 'pointer',
                                    opacity: style.opacity ?? 1,
                                }}
                                imageStyle={{
                                    filter: (style as React.CSSProperties).filter,
                                }}
                            />
                        );
                    })}
                </div>

                {!isMyPiece && selectedPiece && (
                    <div className="monster_option_buttons">
                        <button
                            type="button"
                            className="monster_option_submit"
                            onClick={handleClear}
                        >
                            Clear
                        </button>
                    </div>
                )}
            </div>

            {selectedPiece && (selectedPiece.guess || selectedPiece.player === myPlayerLetter || selectedMonster) ? (
                <MonsterInfo
                    theme={theme}
                    monster={infoMonster}
                    titleLabel="Piece"
                    emptyMessage="Select a board piece to track deductions"
                    imageDirection={imageDirection}
                    onToggleImageDirection={toggleImageDirection}
                />
            ) : (
                <div className="monster_deduction_explanation">
                    <p>
                        Click on a monster to cross it off as a possibility for this piece. When nine species
                        are crossed off, the remaining type is set as the guess automatically.
                    </p>
                </div>
            )}
        </div>
    );
};

export default MonsterDeduction;
